import _includesInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/includes";
import React, { useState, useRef, cloneElement } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useThemeWithUndefined } from '@rescui/ui-contexts';
import { useResizeObserver } from '@rescui/use-resize-observer';
import LoadingIcon from './_virtual/loading.js';
import styles from './button.p.module.css.js';

const calculateShifted = (shiftedProp, side) => shiftedProp === 'all' || shiftedProp === side || Array.isArray(shiftedProp) && _includesInstanceProperty(shiftedProp).call(shiftedProp, side);

const MODE_STYLES = {
  classic: styles.modeClassic,
  rock: styles.modeRock,
  transparent: styles.modeTransparent,
  outline: styles.modeOutline,
  clear: styles.modeClear
};
const SIZE_STYLES = {
  xs: styles.sizeXs,
  s: styles.sizeS,
  m: styles.sizeM,
  l: styles.sizeL
};
const ICON_POSITION_STYLES = {
  left: styles.alignIconLeft,
  'left-edge': styles.alignIconLeftEdge,
  right: styles.alignIconRight,
  'right-edge': styles.alignIconRightEdge
};
const Button = /*#__PURE__*/React.forwardRef( // eslint-disable-next-line complexity
(_ref, forwardedRef) => {
  let {
    className,
    children,
    href,
    type = 'button',
    disabled,
    size = 'm',
    theme: themeFromProps,
    icon,
    iconPosition = 'left',
    busy = false,
    allowMultiline = false,
    defaultMultiline = false,
    flat,
    notFocusable,
    highlighted
    /* supported only for clear mode */
    ,
    shifted
    /* supported only for clear mode */
    ,
    mode = 'classic',
    ...restProps
  } = _ref;
  const [multiline, setMultiline] = useState(defaultMultiline);
  const contentRef = useRef(null);
  const ref = useRef(null);

  const handleRef = el => {
    ref.current = el;

    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(el);
      } else {
        forwardedRef.current = el;
      }
    }
  };

  useResizeObserver(contentRef, entry => {
    const target = entry.target;
    const {
      height
    } = entry.contentRect;
    const targetStyles = getComputedStyle(target);
    const singleLineBlockSize = parseInt(targetStyles.lineHeight, 10);
    setMultiline(height > singleLineBlockSize);
  });
  const theme = useThemeWithUndefined(themeFromProps);
  const shiftedLeft = calculateShifted(shifted, 'left');
  const shiftedRight = calculateShifted(shifted, 'right');
  const shiftedTop = calculateShifted(shifted, 'top');
  const shiftedBottom = calculateShifted(shifted, 'bottom'); // check for ignored jsx values and empty string, more info https://reactjs.org/docs/jsx-in-depth.html#booleans-null-and-undefined-are-ignored

  const isWithoutText = children === null || typeof children === 'undefined' || children === false || children === true || children === '';
  let iconSlot;

  if ( /*#__PURE__*/React.isValidElement(icon) && busy) {
    iconSlot = /*#__PURE__*/React.createElement(LoadingIcon, {
      className: styles.icon,
      "data-render-all-sizes": true
    });
  } else if ( /*#__PURE__*/React.isValidElement(icon)) {
    var _icon$props;

    iconSlot = /*#__PURE__*/cloneElement(icon, {
      className: cn((_icon$props = icon.props) === null || _icon$props === void 0 ? void 0 : _icon$props.className, styles.icon),
      'data-render-all-sizes': true
    });
  }

  const showBusyOverlay = ! /*#__PURE__*/React.isValidElement(icon) && busy;
  const buttonSharedProps = {
    href,
    disabled,
    className: cn(styles.main, MODE_STYLES[mode], SIZE_STYLES[size], ICON_POSITION_STYLES[iconPosition] ?? ICON_POSITION_STYLES.left, styles[theme], {
      [styles.disabled]: disabled,
      [styles.flatLeft]: flat === 'left',
      [styles.flatRight]: flat === 'right',
      [styles.highlighted]: highlighted,
      [styles.withoutText]: isWithoutText,
      [styles.shiftedLeft]: shiftedLeft,
      [styles.shiftedRight]: shiftedRight,
      [styles.shiftedTop]: shiftedTop,
      [styles.shiftedBottom]: shiftedBottom,
      [styles.multiline]: multiline,
      [styles.allowMultiline]: allowMultiline,
      [styles.busy]: busy,
      [styles.busyOverlay]: showBusyOverlay
    }, className)
  }; // Google Translate replaces TextNodes on page which is leading to a React error on manipulating buttonChildren e.g. when switching to the busy state.
  // To prevent this we wrap text node in a span element which itself will not be replaced by Google Translate.
  // More info here: https://github.com/facebook/react/issues/11538

  const buttonContent = !isWithoutText && /*#__PURE__*/React.createElement("span", {
    className: styles.content,
    "data-rs-internal": "button__content",
    "data-test": "button__content",
    ref: contentRef
  }, children);
  const buttonChildren = /*#__PURE__*/React.createElement(React.Fragment, null, iconSlot, showBusyOverlay && /*#__PURE__*/React.createElement(LoadingIcon, {
    className: styles.busyIcon,
    size: size,
    "data-test": "busy-icon"
  }), buttonContent);

  if (notFocusable) {
    if (href) {
      console.error('Button with `href` can\'t be completely not focusable. If you want to remove Button with `href` from focus flow, use `tabIndex="-1"` instead of `notFocusable`');
    }
    /** use <div> tag for preventing button controls elements behavior for example 'space' button press support. */


    return /*#__PURE__*/React.createElement("div", {
      "data-test": 'button',
      ...restProps,
      ref: handleRef,
      ...buttonSharedProps
    }, buttonChildren);
  } else if (href) {
    return /*#__PURE__*/React.createElement("a", {
      "data-test": 'button',
      ...restProps,
      ref: handleRef,
      type: type,
      ...buttonSharedProps
    }, buttonChildren);
  } else {
    return /*#__PURE__*/React.createElement("button", {
      "data-test": 'button',
      ...restProps,
      ref: handleRef,

      /* eslint-disable-next-line react/button-has-type */
      type: type,
      ...buttonSharedProps
    }, buttonChildren);
  }
});
Button.displayName = 'Button';

function validateAdditional(propType, callback) {
  return (props, propName) => {
    if (typeof props[propName] !== 'undefined') {
      const cbRes = callback(props, propName);

      if (cbRes) {
        return cbRes;
      }

      PropTypes.checkPropTypes({
        [propName]: propType
      }, props, 'prop', 'Button');
    }

    return null;
  };
}

Button.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  href: validateAdditional(PropTypes.string, props => {
    if (typeof props.notFocusable !== 'undefined') {
      return new Error(`'href' prop cannot be used with 'notFocusable' prop`);
    }

    return null;
  }),
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'left-edge', 'right', 'right-edge']),
  size: PropTypes.oneOf(['l', 'm', 's', 'xs']),
  mode: validateAdditional(PropTypes.oneOf(['classic', 'rock', 'transparent', 'outline', 'clear']), props => {
    if (typeof props.shifted !== 'undefined' && props.mode !== 'clear') {
      return new Error(`'shifted' prop cannot be used when 'mode' prop is not 'clear'.`);
    }

    return null;
  }),
  theme: PropTypes.oneOf(['light', 'dark']),
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  onClick: PropTypes.func,
  notFocusable: validateAdditional(PropTypes.bool, props => {
    if (typeof props.href !== 'undefined' || typeof props.target !== 'undefined') {
      return new Error(`'notFocusable' prop cannot be used with 'href' and 'target' props`);
    }

    return null;
  }),
  busy: PropTypes.bool,
  flat: PropTypes.oneOf(['left', 'right']),
  highlighted: PropTypes.bool,
  allowMultiline: PropTypes.bool,
  defaultMultiline: PropTypes.bool,
  shifted: PropTypes.oneOfType([PropTypes.oneOf(['all', 'left', 'right', 'top', 'bottom']), PropTypes.arrayOf(PropTypes.oneOf(['left', 'right', 'top', 'bottom']))])
};
export { Button, Button as default };
